import { useCallback } from 'react';
import { PricingTerm } from '@/constants/pricingTypes';
import { useQuoteStore } from './use-quote-store';
import {
  hasValidationErrors,
  mergeApiResponse,
  cancelPendingRequests,
  setPendingController,
  getPendingController,
  clearPendingController,
} from './quote-helpers';
import { updateCoverage } from '@/api/agency-coverage-api';
import { reportError } from '@/services/error-reporting-service';

export const useUpdatePricingTerm = () => {
  const updatePricingTerm = useQuoteStore((state) => state.updatePricingTerm);

  return useCallback(
    async (term: PricingTerm) => {
      const quote = useQuoteStore.getState().quote;
      if (!quote) return;
      if (quote.details.initial_term_selection === term) return;

      // Cancel any pending requests (both attribute and pricing term updates)
      cancelPendingRequests();

      const isMockQuote = quote.details?.quote_source === 'mock';

      updatePricingTerm(term);
      if (isMockQuote) {
        // Mocked quotes (e.g. Elements Gallery) should not trigger repricing
        return;
      }

      const controller = new AbortController();
      setPendingController(controller);

      try {
        const response = await updateCoverage(
          {
            coverages: quote.coverages,
            invoice_period: term,
          },
          { signal: controller.signal }
        );

        // Only merge if this is still the active request
        if (getPendingController() !== controller) return;

        const hasErrors = hasValidationErrors(response);

        mergeApiResponse(response, hasErrors);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        reportError({ error });
        updatePricingTerm(quote.details.initial_term_selection);
      } finally {
        if (getPendingController() === controller) {
          clearPendingController();
        }
      }
    },
    [updatePricingTerm]
  );
};
